/**
 * Phase 86 — the ticket panel My Tasks opens in place.
 *
 * Petra UAT 2026-08-18: clicking a row used to navigate to `/tasks/:taskRef`,
 * and closing that page lands on the project board — so glancing at one ticket
 * cost you the list you were triaging and left you two screens away. The list
 * is the point of this page, so the ticket opens over it and closing returns
 * you to exactly the row you left.
 *
 * The data path is deliberately the same one `TaskDetailPage` uses (locate ->
 * board -> find the card): `TaskDetailSlideOver` needs a full `TaskCard`, which
 * `MyTaskDto` is not, and a second serializer would be one more thing to keep
 * in step with the board's. Both queries are TanStack-cached, so opening a
 * ticket on a project whose board is already loaded costs nothing.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTaskLocation } from '../../hooks/useTaskLocation';
import { useBoard } from '../../hooks/useBoard';
import { useProject } from '../../hooks/useProjects';
import { useAuthOptional } from '../../services/authContext';
import { TaskDetailSlideOver } from '../kanban/TaskDetailSlideOver';
import type { TaskCard as TaskCardData } from '../../services/api/board';
// 260913-jpy — resolves the per-project staleness indicator config from this
// panel's own useProject query, so the slide-over behaves identically
// whichever entry point (board or My Tasks) opened it.
import { projectStalenessConfig } from '../../utils/format';

export interface MyTasksTaskPanelProps {
  /** The task to show, or null when nothing is open. */
  taskRef: string | null;
  onClose: () => void;
}

export function MyTasksTaskPanel({ taskRef, onClose }: MyTasksTaskPanelProps) {
  const navigate = useNavigate();
  // Both hooks stay idle while `taskRef` is null — useTaskLocation is
  // `enabled` on a non-empty ref, and useBoard on a resolved projectId — so
  // nothing is fetched until a row is actually clicked.
  const locationQuery = useTaskLocation(taskRef ?? '');
  const projectId = locationQuery.data?.projectId;
  const boardQuery = useBoard(projectId ? String(projectId) : '', 'internal');
  const projectQuery = useProject(projectId);

  const auth = useAuthOptional();
  const currentUser = auth?.user ?? null;
  const isAdmin = auth?.isAdmin ?? false;
  // Same derivation as TaskDetailPage / KanbanBoardPage: admin OR the PM.
  const canManageMilestones =
    !!currentUser && (isAdmin || projectQuery.data?.projectManager?.id === currentUser.id);
  // 260913-jpy — disabled by default while projectQuery.data is not yet
  // loaded, matching KanbanBoardPage's derivation.
  const stalenessConfig = useMemo(
    () => projectStalenessConfig(projectQuery.data),
    [projectQuery.data],
  );

  const task = useMemo<TaskCardData | null>(() => {
    const taskId = locationQuery.data?.taskId;
    if (taskId == null || !boardQuery.data) return null;
    for (const column of boardQuery.data.columns) {
      const found = column.tasks.find((t) => t.id === taskId);
      if (found) return found;
    }
    return null;
  }, [locationQuery.data, boardQuery.data]);

  if (!taskRef) return null;

  // Loading and not-found both keep the user on the list rather than swapping
  // it for a full-page state — the row they clicked is still behind the panel.
  if (locationQuery.isLoading || boardQuery.isLoading) {
    return (
      <div
        className="fixed inset-y-0 right-0 z-40 w-[420px] max-w-full border-l border-ms-border bg-white px-6 py-5"
        role="status"
      >
        <p className="text-sm text-ms-text-muted">Loading ticket…</p>
      </div>
    );
  }

  if (locationQuery.isError || boardQuery.isError || !task) {
    return (
      <div
        className="fixed inset-y-0 right-0 z-40 w-[420px] max-w-full border-l border-ms-border bg-white px-6 py-5 space-y-3"
        role="alert"
      >
        <h2 className="text-base font-semibold text-ms-text-strong">Ticket not available</h2>
        <p className="text-sm text-ms-text-muted">
          This ticket could not be opened. It may have been deleted, or you may not have access to
          its project.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-primary-600 hover:underline"
        >
          Back to the list
        </button>
      </div>
    );
  }

  return (
    <TaskDetailSlideOver
      task={task}
      audience="internal"
      isOpen
      onClose={onClose}
      canManageMilestones={canManageMilestones}
      projectTaskTypeId={projectQuery.data?.taskTypeId ?? null}
      stalenessConfig={stalenessConfig}
      columns={boardQuery.data?.columns}
      // Michal, #monksheets 2026-08-19 — "Open full screen" is panel-mode-only
      // AND caller-supplied, so opening a ticket from My Tasks lost the icon
      // the board still had. Same target as the board's own callback, so the
      // two entry points behave identically.
      onOpenFullPage={() => navigate(`/tasks/${task.id}`)}
    />
  );
}
